import { useCallback, useEffect, useRef, useState } from "react";

import {
  getRunData,
  getRunLockedFields,
  getRunLockedQuoteFields,
  getRunProspects,
  getRunQuoteValues,
} from "../../lib/workspaceToolsApi";
import { WORKSPACE_TOOL } from "./components/WorkspaceToolRail";
import { WORKSPACE_TOOL_PROJECTION_STATUSES } from "./projectionContract";
import { authorizedByServer, withheld } from "./components/panels/toolProjectionGuard";
import {
  QUOTE_OPTION_INDICES,
  toDiscoveryFields,
  toDiscoveryValues,
  toQuoteFields,
  toQuoteValues,
  toSubjects,
} from "./workspaceToolAdapters";

/**
 * AW-LDQ-003 -- load one tool's governed data for the current Script run.
 *
 * WHAT THIS IS RESPONSIBLE FOR
 * ----------------------------
 * Fetching, mapping a failure to the right withheld status, and -- the
 * part that matters most -- making it impossible for one run's data to
 * appear under another run's identity.
 *
 * RUN IDENTITY IS THE WHOLE SAFETY STORY
 * --------------------------------------
 * `runId` changes on every owned call, and the workspace is a
 * long-lived mount. Three independent mechanisms keep the two aligned,
 * because any one of them alone has a known failure:
 *
 *   1. State is cleared DURING RENDER when `tool` or `runId` changes,
 *      so no frame is ever painted with the previous run's answers
 *      under the new run's identity. An effect would be a frame too
 *      late.
 *   2. Every in-flight response is stamped with the `runId` it was
 *      issued for -- set in `load` before the await, cleared by the
 *      effect teardown -- and discarded if that is no longer current.
 *      Clearing alone does not help if a slow response from call N
 *      lands during call N+1.
 *   3. `null` runId is a first-class state, not an error. Between calls
 *      there is simply nothing to show, and that is said rather than
 *      retried.
 *
 * This is the process-local-state defect family (BB-14F, P2-H7) applied
 * to a browser: whenever local state gates what a user sees about a
 * durable record, assume it is stale until something proves otherwise.
 *
 * NO POLLING, NO WRITES, NO SIDE EFFECTS
 * --------------------------------------
 * One fetch per (run, tool) selection. No interval, no retry loop, no
 * refetch on re-render -- the workspace already polls call state every
 * two seconds and must not gain a second cadence. Nothing here mutates
 * anything: this phase is read-only and the Script runner remains the
 * authoritative writer.
 *
 * (The real runner is named only in `WorkspaceScriptRunner`, which is
 * the one file in this feature permitted to -- a guard enforces it on
 * raw source, comments included, so this prose deliberately does not
 * spell it out.)
 */

/** Tools this hook knows how to load. */
export const DATA_BACKED_TOOLS = Object.freeze([
  WORKSPACE_TOOL.DISCOVERY,
  WORKSPACE_TOOL.QUOTE,
]);

const IDLE = Object.freeze({ loading: false, projectionStatus: null, data: null });
const LOADING = Object.freeze({ loading: true, projectionStatus: null, data: null });

/**
 * Map a failed read to the status the panel should render.
 *
 * The 409 bodies carry AW-LDQ-002's own codes, and they are surfaced as
 * the notice's `reason` so an agent sees WHY rather than a generic
 * unavailability. A 403 is `restricted` because that is precisely what
 * the contract's vocabulary calls it; anything else is `unknown`, which
 * is the honest answer for a transport failure.
 */
export function statusForError(error) {
  const response = error?.response;
  const code = response?.data?.detail?.code || null;

  if (response?.status === 403) {
    return withheld(WORKSPACE_TOOL_PROJECTION_STATUSES.RESTRICTED, code);
  }
  if (response?.status === 404) {
    // The run is outside this Account, or gone. Deliberately the same
    // answer either way -- the backend made them indistinguishable on
    // purpose and this must not undo that.
    return withheld(WORKSPACE_TOOL_PROJECTION_STATUSES.MISSING, null);
  }
  if (response?.status === 409) {
    return withheld(WORKSPACE_TOOL_PROJECTION_STATUSES.MISSING, code);
  }
  return withheld(WORKSPACE_TOOL_PROJECTION_STATUSES.UNKNOWN, code);
}

async function loadDiscovery(runId) {
  const [definitions, values, prospects] = await Promise.all([
    getRunLockedFields(runId),
    getRunData(runId),
    getRunProspects(runId),
  ]);
  return {
    subjects: toSubjects(prospects),
    fields: toDiscoveryFields(definitions),
    values: toDiscoveryValues(values),
  };
}

async function loadQuote(runId) {
  const [definitions, values, prospects] = await Promise.all([
    getRunLockedQuoteFields(runId),
    getRunQuoteValues(runId),
    getRunProspects(runId),
  ]);
  return {
    subjects: toSubjects(prospects),
    options: QUOTE_OPTION_INDICES,
    fields: toQuoteFields(definitions),
    values: toQuoteValues(values),
  };
}

const LOADERS = Object.freeze({
  [WORKSPACE_TOOL.DISCOVERY]: loadDiscovery,
  [WORKSPACE_TOOL.QUOTE]: loadQuote,
});

export default function useWorkspaceToolData({ tool, runId }) {
  const [state, setState] = useState(IDLE);

  // The run each in-flight request was issued for. A response whose
  // stamp no longer matches is dropped rather than rendered.
  const issuedForRef = useRef(null);

  /**
   * The (tool, run) pair the current state belongs to.
   *
   * Reset DURING RENDER rather than in an effect, deliberately. An
   * effect runs after the browser has already been handed a frame, so
   * for one paint the previous run's answers would still be on screen
   * under the new run's identity -- the precise thing this hook exists
   * to prevent. Adjusting state during render is React's own documented
   * answer for "a prop changed and derived state must not survive it",
   * and it costs an immediate re-render rather than a visible one.
   */
  const [loadedFor, setLoadedFor] = useState(null);
  const key = `${tool ?? ""}::${runId ?? ""}`;
  if (loadedFor !== key) {
    setLoadedFor(key);
    // Loading is entered HERE rather than in the effect, for the same
    // reason the clear is: the effect runs a frame later, and the
    // state it would set must not be observable after a paint that
    // still showed the old run. It also keeps the effect free of a
    // synchronous setState, which cascades renders.
    setState(LOADERS[tool] && runId ? LOADING : IDLE);
  }

  const load = useCallback(async (toolKey, currentRunId) => {
    const loader = LOADERS[toolKey];
    if (!loader || !currentRunId) return;

    // A ref write, not state: the stamp must be in place before the
    // first await so a response can be matched against it, and it must
    // not cause a render.
    issuedForRef.current = currentRunId;

    try {
      const data = await loader(currentRunId);
      if (issuedForRef.current !== currentRunId) return;
      setState({ loading: false, projectionStatus: authorizedByServer(), data });
    } catch (error) {
      if (issuedForRef.current !== currentRunId) return;
      setState({ loading: false, projectionStatus: statusForError(error), data: null });
    }
  }, []);

  useEffect(() => {
    if (!runId || !LOADERS[tool]) return undefined;

    // Awaited inside an async wrapper so the await boundary is visible in
    // THIS body rather than hidden one call deep.
    //
    // `react-hooks/set-state-in-effect` traces the call graph, not the
    // await boundary, so a bare `load(tool, runId)` reads to it as a
    // synchronous `setState` even though every `setState` in `load` runs
    // after `await loader(...)`. The directive that used to silence it
    // named a rule only ONE of this repository's two lint toolchains
    // defines: `eslint-plugin-react-hooks@7.0.1` at the top level, which
    // `eslint.config.cjs` and `yarn lint` use, has it; the 4.6.2 copy
    // nested under `react-scripts@5.0.1`, which feeds craco's build-time
    // ESLintWebpackPlugin, defines only `rules-of-hooks` and
    // `exhaustive-deps`. ESLint 8 treats a disable directive naming an
    // unknown rule as an error, so the directive FAILED THE BUILD --
    // "Definition for rule 'react-hooks/set-state-in-effect' was not
    // found" -- while being load-bearing for the other toolchain.
    //
    // This shape needs no directive and is clean under both. It is not the
    // timer the rule would otherwise accept and adds no delay: `await`
    // evaluates its operand before suspending, so `load` is still invoked
    // synchronously here and `issuedForRef.current` is still written
    // before any await, exactly as before. `load` carries a complete
    // try/catch, so the discarded promise can never reject.
    void (async () => {
      await load(tool, runId);
    })();
    return () => {
      // A tool switch or run change invalidates whatever is in flight.
      issuedForRef.current = null;
    };
  }, [tool, runId, load]);

  return state;
}
